import { useState } from "react";
import { Link as RouterLink, useLocation } from "react-router-dom";
import { Box, Drawer, IconButton, Stack } from "@mui/material";
import { Menu as MenuIcon } from "@mui/icons-material";
import { LoginModal } from "../studioxr/auth/AuthButton";
import { useLoginState } from "../studioxr/auth/LoginState";
import links from "./links.json";

// Top nav for signed-out visitors on the app's marketing pages. A copy of the
// Webflow homepage nav, so the header stays the same from mattoboard.com/ into
// /pricing and the SketchUp landings. The links live in links.json, which the
// weekly SEO check compares against the live homepage.

interface NavLink { label: string; href: string; badge?: string; newTab?: boolean }
const NAV_LINKS: NavLink[] = links;

// Webflow collapses its nav into a drawer at this width.
const COLLAPSED = "@media (max-width: 991px)";
const EXPANDED = "@media (min-width: 992px)";
// Just above the collapse the full row is tight: smaller logo, closer links.
const TIGHT = "@media (min-width: 992px) and (max-width: 1100px)";

const linkSx = {
  display: "flex",
  alignItems: "center",
  padding: "7px 12px",
  border: 0,
  background: "none",
  borderRadius: "6px",
  color: "#202939",
  font: "inherit",
  fontSize: 16,
  fontWeight: 400,
  // Fixed, not "normal": the badge's emoji would otherwise deepen its line and drop "NEW!" below the label.
  lineHeight: "24px",
  textTransform: "uppercase",
  textDecoration: "none",
  whiteSpace: "nowrap",
  cursor: "pointer",
  "&:hover": { textDecoration: "underline" },
  [TIGHT]: { padding: "7px 8px" },
};

const Logo = ({ width }: { width: number }) => (
  // A plain link on purpose: only a full load of / reaches the Cloudflare
  // Worker that serves the marketing homepage.
  <Box component="a" href="/" aria-label="Mattoboard home" sx={{ display: "flex", flexShrink: 0 }}>
    <Box component="img" src="/images/mattoboard-logo.jpg" alt="MattoBoard" sx={{ width, [TIGHT]: { width: 160 } }} />
  </Box>
);

export const MarketingNav = () => {
  const { pathname } = useLocation();
  const setShowLogin = useLoginState((state) => state.setShowLogin);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const closeDrawer = () => setDrawerOpen(false);

  const items = (
    <>
      {NAV_LINKS.map(({ label, href, badge, newTab }) => {
        const target = href.startsWith("/")
          ? { component: RouterLink, to: href, "aria-current": pathname === href ? ("page" as const) : undefined }
          : { component: "a", href, ...(newTab && { target: "_blank", rel: "noopener noreferrer" }) };
        return (
          <Box key={href} {...target} onClick={closeDrawer} sx={linkSx}>
            {badge && <Box component="span" sx={{ fontWeight: 500 }}>{badge}</Box>}
            {label}
          </Box>
        );
      })}
      {/* Opens the same signup-first modal as the homepage's SIGN IN. */}
      <Box component="button" type="button" onClick={() => { closeDrawer(); setShowLogin("signup"); }} sx={linkSx}>
        Sign in
      </Box>
    </>
  );

  return (
    <Box component="header" sx={{ flexShrink: 0, height: 76, display: "flex", justifyContent: "center", bgcolor: "#fff", position: "relative", zIndex: 10 }}>
      <Stack
        direction="row"
        sx={{ flex: 1, maxWidth: 1280, boxSizing: "border-box", alignItems: "center", justifyContent: "space-between", gap: 2, px: { xs: 2.5, sm: 5 } }}
      >
        <Logo width={207} />
        <Stack component="nav" direction="row" sx={{ alignItems: "center", [COLLAPSED]: { display: "none" } }}>
          {items}
        </Stack>
        <IconButton aria-label="Open menu" onClick={() => setDrawerOpen(true)} sx={{ color: "#121926", [EXPANDED]: { display: "none" } }}>
          <MenuIcon fontSize="large" />
        </IconButton>
      </Stack>
      <Drawer open={drawerOpen} onClose={closeDrawer} PaperProps={{ sx: { width: 280, maxWidth: "85vw", p: 3 } }}>
        <Logo width={140} />
        <Stack component="nav" sx={{ mt: 3, gap: 1, alignItems: "flex-start" }}>
          {items}
        </Stack>
      </Drawer>
      <LoginModal />
    </Box>
  );
};
